// apps/web/src/lib/vendas/apresentacao.ts — Fase 12, Plano 21 (PV-05, PV-06).
// Transformações puras do detalhe do PV: situação, pendências, lotes, saldo e excedente.
// Classes exatamente da paleta semântica fechada do 12-UI-SPEC (cor nunca é o único sinal:
// todo item tem ícone lucide + texto). Nada aqui recalcula regra de negócio: os saldos vêm da API.
import type { CodigoPendenciaPv, SituacaoPv } from '@pieltec/contracts';
import { formatarQuantidade } from './formatacao';

const CLASSES_SLATE = 'bg-slate-50 text-slate-700 border-slate-200';
const CLASSES_BLUE = 'bg-blue-50 text-blue-700 border-blue-200';
const CLASSES_AMBER = 'bg-amber-50 text-amber-700 border-amber-200';
const CLASSES_ROSE = 'bg-rose-50 text-rose-700 border-rose-200';

export type IconeSituacaoPv = 'CircleDashed' | 'PackageCheck' | 'Factory' | 'CircleDollarSign' | 'CheckCircle2' | 'XCircle';

const SITUACAO: Record<SituacaoPv, { texto: string; icone: IconeSituacaoPv; classes: string }> = {
  ABERTO: { texto: 'Aberto', icone: 'CircleDashed', classes: CLASSES_SLATE },
  EM_RECEBIMENTO: { texto: 'Em recebimento', icone: 'PackageCheck', classes: CLASSES_BLUE },
  EM_PRODUCAO: { texto: 'Em produção', icone: 'Factory', classes: CLASSES_BLUE },
  PARCIALMENTE_FATURADO: { texto: 'Parcialmente faturado', icone: 'CircleDollarSign', classes: 'bg-white text-emerald-700 border-emerald-300' },
  CONCLUIDO: { texto: 'Concluído', icone: 'CheckCircle2', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  CANCELADO: { texto: 'Cancelado', icone: 'XCircle', classes: 'bg-slate-100 text-slate-500 border-slate-200' },
};

/** Texto + ícone + cor da situação derivada do PV. */
export function rotuloSituacaoPv(situacao: SituacaoPv): { texto: string; icone: IconeSituacaoPv; classes: string } {
  return SITUACAO[situacao];
}

export type BucketPendenciaPv = 'rose' | 'amber';
export type IconePendenciaPv = 'Link2Off' | 'CircleDollarSign' | 'TrendingUp' | 'Route';

const PENDENCIA: Record<
  CodigoPendenciaPv,
  { texto: string; icone: IconePendenciaPv; bucket: BucketPendenciaPv; apoio: string | null }
> = {
  MATERIAL_NAO_VINCULADO: { texto: 'Produto sem cadastro', icone: 'Link2Off', bucket: 'rose', apoio: 'Bloqueia OP' },
  SEM_PRECO: { texto: 'Sem preço', icone: 'CircleDollarSign', bucket: 'amber', apoio: null },
  PRECO_DIVERGE: { texto: 'Preço diverge da tabela', icone: 'CircleDollarSign', bucket: 'amber', apoio: null },
  RECEBIDO_A_MAIS: { texto: 'Recebido a mais', icone: 'TrendingUp', bucket: 'amber', apoio: null },
  SEM_ROTEIRO: { texto: 'Sem roteiro', icone: 'Route', bucket: 'rose', apoio: 'Bloqueia OP' },
};

const CLASSES_BUCKET: Record<BucketPendenciaPv, string> = { rose: CLASSES_ROSE, amber: CLASSES_AMBER };

/** Rose = impede um passo do fluxo; amber = atenção sem impedir (mesma semântica da Fase 11). */
export function rotuloPendenciaPv(codigo: CodigoPendenciaPv): {
  texto: string;
  icone: IconePendenciaPv;
  bucket: BucketPendenciaPv;
  apoio: string | null;
  classes: string;
} {
  const base = PENDENCIA[codigo];
  return { ...base, classes: CLASSES_BUCKET[base.bucket] };
}

/** Zero vira a cópia do estado vazio; senão '1 lote' / 'N lotes'. */
export function rotuloContagemLotes(total: number): string {
  if (total <= 0) return 'Nenhum lote recebido para este item ainda.';
  return total === 1 ? '1 lote' : `${total} lotes`;
}

/** Saldo negativo (faturado acima do solicitado) em âmbar; o sinal '−' acompanha (nunca só cor). */
export function classeSaldo(saldo: number): string {
  return saldo < 0 ? 'font-semibold tabular-nums text-amber-700' : 'font-semibold tabular-nums';
}

/** Saldo formatado; negativo leva o sinal de menos tipográfico. */
export function rotuloSaldo(saldo: number, unidade = ''): string {
  const texto = `${formatarQuantidade(Math.abs(saldo), unidade)}${unidade ? ` ${unidade}` : ''}`;
  return saldo < 0 ? `−${texto}` : texto;
}

/** Chip da célula Recebido: '+N a mais'; sem excedente não há chip. */
export function rotuloExcedente(excedente: number, unidade = ''): string | null {
  if (excedente <= 0) return null;
  return `+${formatarQuantidade(excedente, unidade)} a mais`;
}

/**
 * Totais discretos do cabeçalho: soma, por unidade, de um saldo já derivado pela API
 * (itens cancelados ficam de fora). Ex.: '120 peça · 8 kg'; sem itens, '—'.
 */
export function totaisPorUnidade(
  itens: ReadonlyArray<{ cancelado: boolean; unidade: string; saldos: { solicitado: number; faturado: number } }>,
  campo: 'solicitado' | 'faturado',
): string {
  const somas = new Map<string, number>();
  for (const item of itens) {
    if (item.cancelado) continue;
    somas.set(item.unidade, (somas.get(item.unidade) ?? 0) + item.saldos[campo]);
  }
  if (somas.size === 0) return '—';
  return [...somas.entries()].map(([unidade, total]) => `${formatarQuantidade(total, unidade)} ${unidade}`).join(' · ');
}
